import { useState, useEffect } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Users, 
  Edit, 
  Building2, 
  UserCheck,
  UserPlus,
  Search,
  TrendingUp,
  LayoutGrid,
  Table as TableIcon,
  Mail,
  Save,
  HelpCircle
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getInitials } from "@/lib/avatar-utils";
import { TeamMemberSkeleton } from "@/components/ui/team-member-skeleton";
import { AppDataTable } from "@/components/ui/app-data-table";

interface Personnel {
  id: string;
  name: string;
  email: string;
  designation: string | null;
  department_id: string | null;
  department_name: string | null;
  department_color: string | null;
  reports_to: string | null;
  manager_name: string | null;
  manager_designation: string | null;
  governance_level: string | null;
  is_manager: boolean | null;
  roles: string[];
  created_at: string;
  status: 'invited' | 'onboarded';
}

interface Department {
  id: string;
  name: string;
  color: string;
}

const PersonnelManagement = () => {
  const { toast } = useToast();
  const { profile, hasRole } = useAuth();
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [managers, setManagers] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [editingPersonnel, setEditingPersonnel] = useState<Personnel | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>("table");
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [governanceFilter, setGovernanceFilter] = useState("all");
  
  const [formData, setFormData] = useState({
    designation: "",
    department_id: "",
    reports_to: "",
    governance_level: "L3",
    roles: [] as string[]
  });
  
  // Invite states
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"creator" | "reviewer" | "approver" | "admin" | "viewer">("creator");
  const [inviteDesignation, setInviteDesignation] = useState("");
  const [inviteDepartment, setInviteDepartment] = useState("");
  const [inviteReportsTo, setInviteReportsTo] = useState("");
  const [inviteGovernanceLevel, setInviteGovernanceLevel] = useState("L3");
  const [inviteRoles, setInviteRoles] = useState<string[]>([]);

  const isAdmin = hasRole ? hasRole('admin') : false;

  // Load personnel data
  const loadPersonnel = async () => {
    if (!profile?.company_id) return;
    
    setLoading(true);
    try {
      const { data: personnelData, error: personnelError } = await supabase
        .from('profiles')
        .select(`
          id,
          name,
          email,
          designation,
          department_id,
          reports_to,
          governance_level,
          is_manager,
          status,
          created_at,
          updated_at,
          departments (
            name,
            color
          )
        `)
        .eq('company_id', profile.company_id)
        .order('created_at', { ascending: false });

      if (personnelError) throw personnelError;

      const personnelWithRoles = await Promise.all(
        (personnelData || []).map(async (person) => {
          const [rolesResult, managerResult] = await Promise.all([
            supabase
              .from('user_roles')
              .select('role')
              .eq('user_id', person.id),
            person.reports_to ? supabase
              .from('profiles')
              .select('name, designation')
              .eq('id', person.reports_to)
              .single() : Promise.resolve({ data: null })
          ]);
          
          return {
            ...person,
            department_name: person.departments?.name || null,
            department_color: person.departments?.color || null,
            manager_name: managerResult.data?.name || null,
            manager_designation: managerResult.data?.designation || null,
            roles: rolesResult.data?.map(r => r.role) || [],
            status: (person.status || 'onboarded') as 'onboarded' | 'invited'
          };
        })
      );

      // Add invited users
      const { data: invitations } = await supabase
        .from('user_invitations')
        .select('*')
        .eq('company_id', profile.company_id)
        .eq('accepted', false)
        .gt('expires_at', new Date().toISOString());

      const invitedPersonnel = (invitations || []).map(inv => ({
        id: inv.id,
        name: inv.email,
        email: inv.email,
        designation: (inv as any).designation || null,
        department_id: null,
        department_name: null,
        department_color: null,
        reports_to: null,
        manager_name: null,
        manager_designation: null,
        governance_level: null,
        is_manager: null,
        roles: [inv.role],
        created_at: inv.created_at,
        status: 'invited' as const
      }));

      const allPersonnel = [...personnelWithRoles, ...invitedPersonnel];
      setPersonnel(allPersonnel);
      // Update managers list - include ALL onboarded personnel (all personnelWithRoles are onboarded)
      // This allows users to report to anyone in the organization
      // Note: personnelWithRoles are all onboarded users, invitedPersonnel are pending invitations
      setManagers(personnelWithRoles);

    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to load personnel: " + (error.message || "Unknown error"),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Load departments
  const loadDepartments = async () => {
    if (!profile?.company_id) return;
    
    try {
      const { data: departmentsData, error } = await supabase
        .from('departments')
        .select('id, name, color')
        .eq('company_id', profile.company_id)
        .order('name');

      if (error) throw error;
      setDepartments(departmentsData || []);
    } catch (error: any) {
      console.error('Error loading departments:', error);
    }
  };

  useEffect(() => {
    loadPersonnel();
    loadDepartments();
  }, [profile?.company_id]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAdmin) {
      toast({
        title: "Permission Denied",
        description: "Only administrators can manage personnel assignments.",
        variant: "destructive"
      });
      return;
    }
    
    if (!editingPersonnel) return;

    try {
      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          designation: formData.designation || null,
          department_id: formData.department_id || null,
          reports_to: formData.reports_to || null,
          governance_level: formData.governance_level,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingPersonnel.id);

      if (profileError) throw profileError;

      // Update roles
      // First, get current roles
      const { data: currentRoles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', editingPersonnel.id);

      const currentRoleValues = (currentRoles?.map(r => r.role) || []) as string[];
      const rolesToAdd = formData.roles.filter(role => !currentRoleValues.includes(role));
      const rolesToRemove = currentRoleValues.filter(role => !formData.roles.includes(role));

      // Add new roles
      for (const role of rolesToAdd) {
        const { error: addError } = await supabase
          .from('user_roles')
          .insert({ 
            user_id: editingPersonnel.id, 
            role: role as "creator" | "reviewer" | "approver" | "admin" | "viewer",
            company_id: profile?.company_id
          });
        if (addError) throw addError;
      }

      // Remove old roles
      for (const role of rolesToRemove) {
        const { error: removeError } = await supabase
          .from('user_roles')
          .delete()
          .eq('user_id', editingPersonnel.id)
          .eq('role', role as "creator" | "reviewer" | "approver" | "admin" | "viewer");
        if (removeError) throw removeError;
      }

      toast({
        title: "Success",
        description: "Personnel assignment updated successfully."
      });

      // Reset form and close dialog
      setFormData({ designation: "", department_id: "", reports_to: "", governance_level: "L3", roles: [] });
      setEditingPersonnel(null);
      setIsDialogOpen(false);
      await loadPersonnel();
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to update personnel assignment: " + (error.message || "Unknown error"),
        variant: "destructive"
      });
    }
  };

  // Handle edit
  const handleEdit = (person: Personnel) => {
    setEditingPersonnel(person);
    setFormData({
      designation: person.designation || "",
      department_id: person.department_id || "",
      reports_to: person.reports_to || "",
      governance_level: person.governance_level || "L3",
      roles: person.roles || []
    });
    setIsDialogOpen(true);
  };

  // Handle invite
  const handleInvite = async () => {
    if (!inviteEmail || !inviteRole) return;
    
    try {
      // Generate invitation token
      const invitationToken = crypto.randomUUID();
      
      // Insert invitation into database
      const { data: invitation, error } = await supabase
        .from('user_invitations')
        .insert({
          email: inviteEmail,
          role: inviteRole,
          company_id: profile?.company_id,
          invited_by: profile?.id,
          invitation_token: invitationToken,
          designation: inviteDesignation,
          department_id: inviteDepartment || null,
          reports_to: inviteReportsTo || null,
          governance_level: inviteGovernanceLevel
        })
        .select()
        .single();

      if (error) throw error;

      // Get company name for email
      const { data: companyData } = await supabase
        .from('companies')
        .select('name')
        .eq('id', profile?.company_id)
        .single();

      // Send invitation email via edge function
      try {
        // Get the current site URL dynamically
        const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
        
        const { data: emailResponse, error: emailError } = await supabase.functions.invoke('send-invitation', {
          body: {
            email: inviteEmail,
            companyName: companyData?.name || 'Your Company',
            role: inviteRole,
            invitationToken: invitationToken,
            invitedByName: profile?.name || 'Admin',
            siteUrl: siteUrl
          }
        });

        if (emailError) {
          console.error('Email sending failed:', emailError);
          // Still show success but mention email issue
          toast({
            title: "Invitation created",
            description: "Invitation created but email sending failed. Please share the invitation link manually.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Invitation sent!",
            description: `Invitation email sent to ${inviteEmail}`
          });
        }
      } catch (error) {
        console.error('Error sending invitation email:', error);
        toast({
          title: "Invitation created",
          description: "Invitation created but email sending failed. Please share the invitation link manually.",
          variant: "destructive"
        });
      }

      setInviteEmail("");
      setInviteDesignation("");
      setInviteDepartment("");
      setInviteReportsTo("");
      setInviteGovernanceLevel("L3");
      setInviteRoles([]);
      setIsInviteDialogOpen(false);
      loadPersonnel();
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to create invitation: " + (error.message || "Unknown error"),
        variant: "destructive"
      });
    }
  };

  // Filter personnel
  const filteredPersonnel = personnel.filter(person => {
    const matchesSearch = 
      person.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      person.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (person.designation && person.designation.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesDepartment = departmentFilter === "all" || person.department_name === departmentFilter;
    const matchesRole = roleFilter === "all" || person.roles.includes(roleFilter);
    const matchesGovernance = governanceFilter === "all" || person.governance_level === governanceFilter;
    
    return matchesSearch && matchesDepartment && matchesRole && matchesGovernance;
  });

  // Get governance level color
  const getGovernanceLevelColor = (level: string | null) => {
    switch (level) {
      case 'L1': return 'bg-red-100 text-red-800 border-red-200';
      case 'L2': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'L3': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'L4': return 'bg-green-100 text-green-800 border-green-200';
      case 'L5': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const personnelColumns: ColumnDef<Personnel>[] = [
    {
      id: "member",
      header: "Member",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <Avatar className="w-10 h-10">
            <AvatarFallback>{getInitials(row.original.name)}</AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">{row.original.name}</div>
            <div className="text-sm text-muted-foreground">{row.original.email}</div>
            {row.original.designation && <div className="text-xs text-muted-foreground">{row.original.designation}</div>}
          </div>
        </div>
      ),
      enableSorting: false,
    },
    {
      id: "role",
      header: "Role",
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.roles.map((role) => (
            <Badge key={role} variant="outline">{role}</Badge>
          ))}
          {row.original.status === "invited" && <Badge variant="secondary">Invited</Badge>}
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "department_name",
      header: "Department",
      cell: ({ row }) =>
        row.original.department_name ? (
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: row.original.department_color || "#gray" }} />
            <span>{row.original.department_name}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">Unassigned</span>
        ),
    },
    {
      accessorKey: "manager_name",
      header: "Reports To",
      cell: ({ row }) =>
        row.original.manager_name ? (
          <div className="flex items-center gap-1">
            <TrendingUp className="w-3 h-3 text-muted-foreground" />
            <span className="text-sm">{row.original.manager_name}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      accessorKey: "governance_level",
      header: "Level",
      cell: ({ row }) =>
        row.original.governance_level ? (
          <Badge className={getGovernanceLevelColor(row.original.governance_level)}>
            {row.original.governance_level}
          </Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="text-right">
          {row.original.status !== "invited" && (
            <Button variant="ghost" size="sm" onClick={() => handleEdit(row.original)}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      ),
      meta: { headerClassName: "text-right", cellClassName: "text-right" },
      enableSorting: false,
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <TeamMemberSkeleton key={i} variant="card" />
            ))}
          </div>
        ) : (
          <Card className="shadow-card">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Personnel</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead>Governance Level</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TeamMemberSkeleton variant="table" />
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">Admin Access Required</h3>
            <p className="text-muted-foreground">
              Only administrators can manage personnel assignments and reporting relationships.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="shadow-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Team Management
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Manage team members, assignments, and organizational structure.
              </p>
            </div>
            <Button onClick={() => setIsInviteDialogOpen(true)}>
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Member
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search by name, email, or designation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="creator">Creator</SelectItem>
                <SelectItem value="reviewer">Reviewer</SelectItem>
                <SelectItem value="approver">Approver</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
            <Select value={governanceFilter} onValueChange={setGovernanceFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Governance" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="L1">L1 - Executive</SelectItem>
                <SelectItem value="L2">L2 - Senior Mgmt</SelectItem>
                <SelectItem value="L3">L3 - Management</SelectItem>
                <SelectItem value="L4">L4 - Senior IC</SelectItem>
                <SelectItem value="L5">L5 - IC</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button
                variant={viewMode === 'table' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('table')}
              >
                <TableIcon className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="icon"
                onClick={() => setViewMode('grid')}
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading team members...</p>
            </div>
          ) : filteredPersonnel.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No team members found</h3>
              <p className="text-muted-foreground">Try adjusting your search or filter criteria.</p>
            </div>
          ) : viewMode === 'table' ? (
            <div className="border rounded-lg">
              <AppDataTable
                columns={personnelColumns}
                data={filteredPersonnel}
                emptyMessage="No team members found."
                getRowId={(person) => person.id}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPersonnel.map((person) => (
                <Card key={person.id} className="relative group hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Avatar className="w-12 h-12 flex-shrink-0">
                        <AvatarFallback>
                          {getInitials(person.name)}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <h3 className="font-medium text-foreground truncate">{person.name}</h3>
                            {person.is_manager && (
                              <Badge variant="outline" className="text-xs flex-shrink-0">
                                <UserCheck className="w-3 h-3 mr-1" />
                                Manager
                              </Badge>
                            )}
                          </div>
                          <Badge className={`${getGovernanceLevelColor(person.governance_level)} flex-shrink-0`}>
                            {person.governance_level || 'L3'}
                          </Badge>
                        </div>
                        
                        <p className="text-sm text-muted-foreground truncate">{person.designation || 'No designation'}</p>
                        <p className="text-xs text-muted-foreground truncate">{person.email}</p>
                        
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-1 text-sm">
                            <Building2 className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                            <span className="text-muted-foreground truncate">
                              {person.department_name || 'Unassigned'}
                            </span>
                            {person.department_color && (
                              <div 
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: person.department_color }}
                              />
                            )}
                          </div>
                          
                          {person.manager_name && (
                            <div className="flex items-center gap-1 text-sm">
                              <TrendingUp className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                              <span className="text-muted-foreground truncate">
                                Reports to {person.manager_name}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(person)}
                          className="w-full mt-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Edit className="w-3 h-3 mr-2" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Personnel Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        setIsDialogOpen(open);
        if (!open) {
          setEditingPersonnel(null);
          setFormData({ designation: "", department_id: "", reports_to: "", governance_level: "L3", roles: [] });
        }
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Personnel Assignment</DialogTitle>
            <DialogDescription>
              Update department assignment and reporting relationship for {editingPersonnel?.name}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="designation">Designation</Label>
                <Input
                  id="designation"
                  value={formData.designation}
                  onChange={(e) => setFormData(prev => ({ ...prev, designation: e.target.value }))}
                  placeholder="e.g. Product Manager, CEO"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Select value={formData.department_id || "none"} onValueChange={(value) => setFormData(prev => ({ ...prev, department_id: value === "none" ? "" : value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Department</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: dept.color }}
                          />
                          {dept.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reports_to">Reports To</Label>
                <Select value={formData.reports_to || "none"} onValueChange={(value) => setFormData(prev => ({ ...prev, reports_to: value === "none" ? "" : value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select manager">
                      {formData.reports_to && formData.reports_to !== "none" 
                        ? managers.find(m => m.id === formData.reports_to)?.name || "Select manager"
                        : "No Manager"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Manager</SelectItem>
                    {managers
                      .filter(m => m.id && m.id !== editingPersonnel?.id)
                      .map((manager) => (
                        <SelectItem key={manager.id} value={manager.id}>
                          {manager.name || manager.email} {manager.designation ? `- ${manager.designation}` : ''}
                        </SelectItem>
                      ))}
                    {managers.length === 0 && (
                      <SelectItem value="loading" disabled>Loading team members...</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="governance_level">Governance Level</Label>
                <Select value={formData.governance_level || "L3"} onValueChange={(value) => setFormData(prev => ({ ...prev, governance_level: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select governance level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="L1">L1 - Executive Leadership</SelectItem>
                    <SelectItem value="L2">L2 - Senior Management</SelectItem>
                    <SelectItem value="L3">L3 - Management</SelectItem>
                    <SelectItem value="L4">L4 - Senior Individual Contributor</SelectItem>
                    <SelectItem value="L5">L5 - Individual Contributor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="roles">Roles</Label>
                <div className="grid grid-cols-2 gap-2">
                  {['admin', 'creator', 'reviewer', 'approver', 'viewer'].map(role => (
                    <label key={role} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.roles.includes(role)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData(prev => ({ ...prev, roles: [...prev.roles, role] }));
                          } else {
                            setFormData(prev => ({ ...prev, roles: prev.roles.filter(r => r !== role) }));
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm capitalize">{role}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Update Assignment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join your team with organizational details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email *</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="user@example.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-designation">Designation</Label>
              <Input
                id="invite-designation"
                value={inviteDesignation}
                onChange={(e) => setInviteDesignation(e.target.value)}
                placeholder="e.g. Product Manager, CEO"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="invite-department">Department</Label>
              <Select value={inviteDepartment || "none"} onValueChange={(value) => setInviteDepartment(value === "none" ? "" : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Department</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: dept.color }}
                        />
                        {dept.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="invite-reports-to">Reports To</Label>
              <Select value={inviteReportsTo || "none"} onValueChange={(value) => setInviteReportsTo(value === "none" ? "" : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Manager</SelectItem>
                  {managers.map((manager) => (
                    <SelectItem key={manager.id} value={manager.id}>
                      {manager.name} - {manager.designation || 'Manager'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="invite-governance-level">Governance Level</Label>
              <Select value={inviteGovernanceLevel} onValueChange={setInviteGovernanceLevel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="L1">L1 - Executive Leadership</SelectItem>
                  <SelectItem value="L2">L2 - Senior Management</SelectItem>
                  <SelectItem value="L3">L3 - Management</SelectItem>
                  <SelectItem value="L4">L4 - Senior Individual Contributor</SelectItem>
                  <SelectItem value="L5">L5 - Individual Contributor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Role *</Label>
              <Select value={inviteRole} onValueChange={(value) => setInviteRole(value as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="creator">Creator</SelectItem>
                  <SelectItem value="reviewer">Reviewer</SelectItem>
                  <SelectItem value="approver">Approver</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Note: Additional roles can be assigned after the member accepts the invitation.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => {
              setIsInviteDialogOpen(false);
              setInviteEmail("");
              setInviteDesignation("");
              setInviteDepartment("");
              setInviteReportsTo("");
              setInviteGovernanceLevel("L3");
              setInviteRoles([]);
            }}>
              Cancel
            </Button>
            <Button type="button" onClick={handleInvite} disabled={!inviteEmail || !inviteRole}>
              <Mail className="w-4 h-4 mr-2" />
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Help Section */}
      <Card className="shadow-card">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <HelpCircle className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div className="space-y-2">
              <h3 className="font-medium text-foreground">About Personnel Management</h3>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>• <strong>Governance Levels:</strong> L1 (Executive) → L2 (Senior Management) → L3 (Management) → L4 (Senior IC) → L5 (IC)</p>
                <p>• <strong>Reporting Structure:</strong> Define who reports to whom for organizational hierarchy</p>
                <p>• <strong>Department Assignment:</strong> Assign team members to departments for better organization</p>
                <p>• <strong>Manager Status:</strong> Automatically set based on governance level (L1-L3 are typically managers)</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PersonnelManagement;
